import { useEffect, useRef, useState } from "react";
import { StatusPill } from "../helpers/StatusPill";
import { CourtScene } from "./CourtroomCardThumb";
import type { CourtCardProps } from "../helpers/types";
import { Eye, Clock, Play, Pause } from "lucide-react";
import Hls from "hls.js";
import logger from "../../../../utils/logger";
export function CourtCard({
  room,
  idx,
  isPlaying,
  onPlay,
  snapUrl,
  onStop,
  roomFlags,
  onCardClick,
}: Readonly<CourtCardProps>) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const flashRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const isOffline = room.status === "offline";
  const canPlay = !isOffline && !!room.hlsUrl;
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFlagged =
    room.status === "flagged" ||
    (room.status === "live" &&
      (room.flags?.some((f) => !f.resolved) ?? false)) ||
    (roomFlags?.some((f) => !f.rv && f.flagStatus !== "dismissed") ?? false);
  useEffect(() => {
    const onTouchStart = () => setIsTouchActive(true);
    const onMouseMoveGlobal = () => setIsTouchActive(false);
    globalThis.addEventListener("touchstart", onTouchStart, { passive: true });
    globalThis.addEventListener("mousemove", onMouseMoveGlobal, {
      passive: true,
    });
    return () => {
      globalThis.removeEventListener("touchstart", onTouchStart);
      globalThis.removeEventListener("mousemove", onMouseMoveGlobal);
    };
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !room.hlsUrl) return;
    if (!isPlaying) {
      // Tear down
      hlsRef.current?.destroy();
      hlsRef.current = null;
      video.src = "";
      return;
    }
    if (room.hlsUrl.includes(".m3u8")) {
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hls.loadSource(room.hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = room.hlsUrl;
        video.play().catch(() => {});
      }
    } else {
      video.src = room.hlsUrl;
      video.play().catch(() => {});
    }
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
      video.src = "";
    };
  }, [isPlaying, room.hlsUrl]);
  let borderStyle: React.CSSProperties = { border: "1px solid #e5e7eb" };
  if (isPlaying) {
    borderStyle = {
      border: "2px solid #1A56DB",
      boxShadow: "0 0 0 3px rgba(26,86,219,.12)",
    };
  } else if (isFlagged) {
    borderStyle = {
      border: "2px solid #EF4444",
      boxShadow: "0 0 0 3px rgba(239,68,68,.12)",
    };
  }
  const playOverlayClass = isPlaying
    ? "opacity-100 bg-transparent"
    : "opacity-0 bg-black/20 hover:opacity-100";

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-play-btn]")) {
      if (!canPlay) return;
      if (isPlaying) {
        onStop();
      } else {
        onPlay(idx);
      }
      return;
    }
    if (isOffline) return;
    onCardClick(idx);
  };

  const handlePlayBtnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canPlay) return;
    if (isPlaying) {
      onStop();
    } else {
      onPlay(idx);
    }
  };

  logger.log("is", isTouchActive);

  return (
    <div
      ref={cardRef}
      tabIndex={0}
      className="crc-tilt relative rounded-[10px] bg-white overflow-hidden p-0 text-left"
      style={{
        ...borderStyle,
      }}
      onMouseEnter={() => {
        if (!canPlay) return;
        hoverTimerRef.current = setTimeout(() => onPlay(idx), 400);
      }}
      onClick={handleCardClick}
    >
      <div className="relative h-32">
        <CourtScene room={room} idx={idx} snapUrl={snapUrl} />
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ display: isPlaying ? "block" : "none" }}
          muted
          playsInline
          controls={false}
        />
        <div
          ref={flashRef}
          className="absolute inset-0 pointer-events-none"
          style={{ background: "#fff", opacity: 0 }}
        />
        {/* Top badge */}
        <div className="absolute top-1.5 left-2 text-[10px] text-white bg-black/60 px-2 py-0.5 rounded flex items-center gap-1">
          {(room.status === "live" || room.status === "flagged") &&
            !isOffline && (
              <span
                className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"
                style={{ animation: "bl 1.2s ease-in-out infinite" }}
                aria-hidden="true"
              />
            )}
          {room.status === "rec" && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0"
              aria-hidden="true"
            />
          )}
          {room.id} · {room.blk}
        </div>
        {/* Flag */}
        {isFlagged && (
          <div className="absolute top-1.5 right-2 text-[9px] bg-red-500 text-white px-2 py-0.5 rounded">
            ⚑ {room.flagType}
          </div>
        )}
        {(room.vw ?? 0) > 0 && (
          <div className="absolute bottom-2 left-2 text-white/70 text-[10px] flex items-center gap-1">
            <Eye size={10} aria-hidden="true" /> {room.vw}
          </div>
        )}
        {/* Duration */}
        {!isOffline && (
          <div className="absolute bottom-2 right-2 text-white/50 text-[10px] tabular-nums">
            {room.dur}
          </div>
        )}
        {/* Play Overlay */}
        <div
          className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-all duration-200 ${playOverlayClass}`}
        >
          <button
            type="button"
            onClick={handlePlayBtnClick}
            data-play-btn
            className="pointer-events-auto w-9 h-9 rounded-full flex items-center justify-center bg-white/88 hover:scale-105 transition"
          >
            {isPlaying ? (
              <Pause size={16} className="text-gray-800" />
            ) : (
              <Play size={16} className="text-gray-800" />
            )}
          </button>
        </div>
      </div>

      {/* Info */}

      <div className="p-2">
        <div className="flex justify-between items-start gap-1">
          <span className="text-xs font-medium truncate max-w-28">
            {isOffline ? "—" : room.cs}
          </span>
          <StatusPill st={room.status} />
        </div>
        <div className="text-[11px] text-gray-500 truncate">{room.jg}</div>
        {isOffline && room.nextSession && (
          <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-1">
            <Clock size={10} aria-hidden="true" />
            Next: {room.nextSession}
          </div>
        )}
      </div>
    </div>
  );
}
